import { useEffect, useState } from 'react';
import { getJson } from '../api/client';
import { Alert, Badge, EmptyState, Spinner } from '../components/ui';
import { brl, dataBr, mesAno } from '../lib/format';
import styles from './screens.module.css';

interface Competencia {
  id: number;
  ano: number;
  mes: number;
}
interface Pendente {
  demonstrativoId: number;
  unidadeNumero: string;
  situacao: string;
  vencimento: string | null;
  total: number;
}

type Carga =
  | { estado: 'carregando' }
  | { estado: 'erro' }
  | { estado: 'ok'; competencia: Competencia | null; total: number; pendentes: Pendente[] };

const SITUACAO_LABEL: Record<string, string> = {
  aberto: 'Em aberto',
  atrasado: 'Atrasado',
  divergente: 'Divergente',
};

/** Competência pedida na URL (`/recebimentos?competencia=ID`, do atalho "Ver quem falta"). */
function competenciaDaUrl(): number | null {
  const id = Number(new URLSearchParams(window.location.search).get('competencia'));
  return Number.isInteger(id) && id > 0 ? id : null;
}

/**
 * Unidades que ainda não pagaram a fatura da competência (#131). Somente leitura
 * para síndico e contábil: a API (`GET /api/competencias/:id/pendentes`) é de ambos.
 */
export function Recebimentos() {
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando' });

  useEffect(() => {
    void (async () => {
      const lista = await getJson<{ competencias?: Competencia[] }>('/api/competencias');
      if (!lista) {
        setCarga({ estado: 'erro' });
        return;
      }
      const todas = lista.competencias ?? [];
      const pedida = competenciaDaUrl();
      // Sem ?competencia= (ou com id desconhecido) usa a mais recente, a primeira da lista.
      const competencia = todas.find((c) => c.id === pedida) ?? todas[0] ?? null;
      if (!competencia) {
        setCarga({ estado: 'ok', competencia: null, total: 0, pendentes: [] });
        return;
      }
      const dados = await getJson<{ total: number; pendentes: Pendente[] }>(
        `/api/competencias/${encodeURIComponent(competencia.id)}/pendentes`,
      );
      setCarga(
        dados
          ? { estado: 'ok', competencia, total: dados.total, pendentes: dados.pendentes }
          : { estado: 'erro' },
      );
    })();
  }, []);

  if (carga.estado === 'carregando') return <Spinner />;
  if (carga.estado === 'erro') {
    return <Alert kind="danger">Não foi possível carregar os recebimentos.</Alert>;
  }
  if (!carga.competencia) return <EmptyState>Nenhuma competência aberta.</EmptyState>;

  return (
    <div className={styles.stack}>
      <p>Competência {mesAno(carga.competencia)}</p>
      {carga.total === 0 ? (
        <EmptyState>Nenhuma fatura gerada para esta competência.</EmptyState>
      ) : carga.pendentes.length === 0 ? (
        <EmptyState>Todas as unidades pagaram.</EmptyState>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Unidade</th>
                <th>Situação</th>
                <th>Vencimento</th>
                <th className={styles.num}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {carga.pendentes.map((p) => (
                <tr key={p.demonstrativoId}>
                  <td>{p.unidadeNumero}</td>
                  <td>
                    <Badge tone={p.situacao === 'atrasado' ? 'warning' : undefined}>
                      {SITUACAO_LABEL[p.situacao] ?? p.situacao}
                    </Badge>
                  </td>
                  <td>{p.vencimento ? dataBr(p.vencimento) : '—'}</td>
                  <td className={styles.num}>{brl(p.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
